import type ConstructorIOBrowser from '@constructor-io/constructorio-client-javascript'
import type { RecommendationsResponse, AutocompleteResponse, IAutocompleteParameters, SearchResponse, BrowseResponse } from '@constructor-io/constructorio-client-javascript'
import { useHttpStore } from '@/js/stores/httpStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import type { CnstrcRecommendationOptions, CnstrcSearchPreflight, IRogue, IRogueCnstrc } from '@/js/services/RogueTypes'
import type { IReflektionProcessedResponse } from '@/js/services/rfk/types'
import { processConstructorProduct, modifyFacetValues, facetValueTextTransform, processRefinedContent } from './util'
import { normalizeFacetDisplayName } from '../rfk/util'
import { SECTIONS, SEARCH_FACET_ALLOWLIST, SEARCH_SUGGESTION_COUNT } from './constants'
import type { INodeClient, CnstrcQueryOptions } from './types'

export type { CnstrcQueryOptions }

type SyncOrAsync = IReflektionProcessedResponse | Promise<IReflektionProcessedResponse>

// The Node SDK has no default request timeout and isn't wired to the render's AbortController (EDEV-3563); kept under SERVER_CONFIG.renderTimeout (5000ms).
const CONSTRUCTOR_REQUEST_TIMEOUT_MS = 3000

// Node SDK clients hold no per-request state (user identity is passed per method call), so we
// build one per index key and share it process-wide instead of reallocating on every SSR render.
const nodeClientCache = new Map<string, Promise<INodeClient | undefined>>()

// Constructor documents each `related_searches` entry as `{ query }`, but the SDK types the
// array as an untyped record, so the other spellings are accepted rather than trusted.
const extractRelatedSearches = (raw: any): string[] =>
  (Array.isArray(raw) ? raw : [])
    .map((item: any) => (typeof item === 'string' ? item : item?.query ?? item?.value ?? item?.term))
    .filter((term: any): term is string => typeof term === 'string' && term.trim().length > 0)

export class RogueConstructor implements IRogueCnstrc {
  rogue: IRogue | undefined
  inFlight: { [key: string]: Promise<IReflektionProcessedResponse> } = {}

  private sdkClient?: ConstructorIOBrowser
  private sdkClientPromise?: Promise<ConstructorIOBrowser | undefined>
  private nodeClientPromise?: Promise<INodeClient | undefined>

  init(rogue: IRogue) {
    this.rogue = rogue
    if (!rogue.keys.constructor?.indexKey) return

    if (rogue.ssr) {
      this.nodeClientPromise = this.loadNodeClient()
    } else {
      this.sdkClientPromise = this.loadSdkClient()
    }
  }

  private getIndexKey(): string {
    const rogue = this.rogue!
    const lang = rogue.l10n.lang
    const config = rogue.keys.constructor!

    return config.languageKeys?.[lang] ?? config.indexKey
  }

  // Builds the userParameters object for SSR Node SDK calls from the per-request
  // SSR context (cookies + request headers).
  private buildNodeUserParams(): import('./types').CnstrcNodeUserParameters {
    const cnstrc = this.rogue?.ssrContext?.cnstrc

    if (!cnstrc) return { clientId: 'ssr-fallback' }
    
    return {
      clientId  : cnstrc.clientId,
      sessionId : cnstrc.sessionId,
      userIp    : cnstrc.userIp,
      userAgent : cnstrc.userAgent,
    }
  }

  private loadNodeClient(): Promise<INodeClient | undefined> {
    // import.meta.env.SSR is replaced with `false` in browser builds by Vite,
    // making this block dead code that gets tree-shaken out of the browser bundle.
    if (!import.meta.env.SSR) return Promise.resolve(undefined)

    // Reuse the process-shared client for this index key (see nodeClientCache).
    const indexKey = this.getIndexKey()
    const cached = nodeClientCache.get(indexKey)
    if (cached) return cached

    const clientPromise = this.createNodeClient(indexKey)
    nodeClientCache.set(indexKey, clientPromise)

    // Don't let a transient construction failure disable the Node client for this
    // index key for the whole process lifetime — evict on failure so the next render retries.
    clientPromise
      .then(client => { if (!client) nodeClientCache.delete(indexKey) })
      .catch(() => nodeClientCache.delete(indexKey))

    return clientPromise
  }

  private async createNodeClient(indexKey: string): Promise<INodeClient | undefined> {
    const rogue = this.rogue!
    try {
      const { default: ConstructorioNode } = await import('@constructor-io/constructorio-node')
      const securityToken = process.env.CONSTRUCTOR_SECURITY_TOKEN

      return new ConstructorioNode({
        apiKey            : indexKey,
        ...(securityToken ? { securityToken } : {}),
        networkParameters : { timeout: CONSTRUCTOR_REQUEST_TIMEOUT_MS },
      }) as INodeClient
    } catch (err) {
      rogue.log.warn('Failed to initialize Constructor.io Node SDK', err as Record<string, any>)

      return undefined
    }
  }

  private async loadSdkClient(): Promise<ConstructorIOBrowser | undefined> {
    const rogue = this.rogue!
    try {
      const { default: ConstructorioClient } = await import('@constructor-io/constructorio-client-javascript')
      this.sdkClient = new ConstructorioClient({
        apiKey    : this.getIndexKey(),
        userId    : rogue.pinia ? useCustomerStore(rogue.pinia).rogueId2 || undefined : undefined,
        segments  : (window as any).cnstrc?.userSegments,
        // Store session ID as a cookie so SSR can read it on the next request.
        // Default is 'local' (localStorage), which is inaccessible server-side.
        idOptions : { session_id_storage_location: 'cookie' },
      })

      rogue.log.crumb('cnstrc', 'Browser SDK initialized')

      return this.sdkClient
    } catch (err) {
      rogue.log.warn('Failed to initialize Constructor.io Browser SDK', err as Record<string, any>)

      return undefined
    }
  }

  // Sets (or clears, when passed []) the live SDK's user segments — pushed by customerStore when the customer's segments/gender change.
  async setSegments(segments: string[]): Promise<void> {
    const client = await (this.sdkClientPromise ?? Promise.resolve(this.sdkClient))
    // apiKey is only re-passed to satisfy the (over-strict) type; setClientOptions merges, so it's a no-op.
    client?.setClientOptions({ apiKey: this.getIndexKey(), segments })
  }

  // Handles the SSR-cache-aware fetch pattern shared by search, browse, and
  // recommendations: returns the cached value synchronously on client hydration,
  // deduplicates in-flight requests, writes to the httpStore on SSR, and
  // dispatches to the correct SDK (Node vs Browser) based on context.
  //
  // `useSharedCache` gates only the cross-request SSR cache (Redis today). The httpStore
  // write below always happens — that's per-render SSR→client hydration state, not a cache.
  // `fresh` makes a client call skip that hydration state and ask Constructor again.
  private fetchWithCache(
    cacheKey: string,
    label: string,
    nodeFetch: (client: INodeClient) => Promise<IReflektionProcessedResponse>,
    sdkFetch: (client: ConstructorIOBrowser) => Promise<IReflektionProcessedResponse>,
    useSharedCache = true,
    fresh = false
  ): SyncOrAsync {
    const rogue = this.rogue!
    const httpStore = useHttpStore(rogue.pinia)

    if (!rogue.ssr && !fresh && httpStore.cache[cacheKey]) {
      rogue.log.debug(`Loading Constructor ${label} from SSR-supplied Initial State: ${cacheKey}`)

      return httpStore.cache[cacheKey] as IReflektionProcessedResponse
    }

    if (this.inFlight[cacheKey] !== undefined) return this.inFlight[cacheKey]

    if (rogue.ssrContext?.isLoadTest && rogue.ssr) return this.emptyResponse(label)

    const clear = (response: IReflektionProcessedResponse) => {
      delete this.inFlight[cacheKey]

      return response
    }
    const fail = (err: any) => {
      delete this.inFlight[cacheKey]
      rogue.log.error(`Constructor.io ${label} failed`, err)

      return Promise.reject(err)
    }

    if (rogue.ssr) {
      this.inFlight[cacheKey] = this.ssrFetch(cacheKey, label, nodeFetch, useSharedCache)
        .then(response => {
          httpStore.setCache(cacheKey, response)

          return clear(response)
        })
        .catch(fail)

      return this.inFlight[cacheKey]
    }

    this.inFlight[cacheKey] = (this.sdkClientPromise ?? Promise.resolve(this.sdkClient))
      .then(client => {
        if (!client) {
          rogue.log.warn(`Constructor.io Browser SDK unavailable, skipping ${label}`)

          return this.emptyResponse(label)
        }

        return sdkFetch(client)
      })
      .then(response => {
        httpStore.setCache(cacheKey, response)

        return clear(response)
      })
      .catch(fail)

    return this.inFlight[cacheKey]
  }

  // node-fetch names its abort error 'AbortError' — the only reliable way to tell our timeout fired vs. a real Constructor.io error.
  private isNodeClientTimeout(err: unknown): boolean {
    return (err as { name?: string } | undefined)?.name === 'AbortError'
  }

  private async ssrFetch(
    cacheKey: string,
    label: string,
    nodeFetch: (client: INodeClient) => Promise<IReflektionProcessedResponse>,
    useSharedCache = true
  ): Promise<IReflektionProcessedResponse> {
    if (!import.meta.env.SSR) return this.emptyResponse(label)

    const rogue = this.rogue!
    const cache = rogue.ssrContext?.cache
    const redisKey = `${rogue.site.code}-${cacheKey}`

    // Skipped entirely rather than just on write: an uncached key would only ever
    // produce guaranteed-miss round-trips.
    if (useSharedCache && cache) {
      if (rogue.ssrContext?.renderUrl?.includes('FLUSH_SSR_REDIS')) {
        await cache.delete(redisKey)
      } else {
        const cached = await cache.get(redisKey)
        if (cached !== null && cached !== undefined) {
          rogue.log.debug(`Constructor ${label} Redis cache hit: ${redisKey}`)

          return cached as IReflektionProcessedResponse
        }
      }
    }

    const client = await this.nodeClientPromise
    if (!client) {
      rogue.log.warn(`Constructor.io Node SDK unavailable, skipping SSR ${label}`)

      return this.emptyResponse(label)
    }

    let response: IReflektionProcessedResponse
    try {
      response = await nodeFetch(client)
    } catch (err) {
      if (this.isNodeClientTimeout(err)) {
        rogue.log.error(
          `Constructor.io Node SDK request timed out after ${CONSTRUCTOR_REQUEST_TIMEOUT_MS}ms: ${label}`,
          { site: rogue.site.code, cacheKey, redisKey } as Record<string, any>
        )
      }

      throw err
    }

    // Constructor cache length from Growthbook flag `constructor_cache_seconds` (seconds); no caching if flag is not set.
    const cacheSeconds = Number(rogue.featureFlags.eval('constructor_cache_seconds')) || 0
    if (useSharedCache && cache && cacheSeconds > 0) {
      cache.set(redisKey, response, cacheSeconds * 1000)
        .catch((err: unknown) => rogue.log.warn('Failed to write Constructor response to Redis', err as Record<string, any>))
    }

    return response
  }

  private buildQueryCacheKey(prefix: string, options: CnstrcQueryOptions): string {
    const parts = [prefix]
    if (options.page) parts.push(`p${options.page}`)
    if (options.numResults) parts.push(`n${options.numResults}`)
    if (options.sortBy) parts.push(`s${options.sortBy}_${options.sortOrder ?? 'asc'}`)
    if (options.filters && Object.keys(options.filters).length) parts.push(JSON.stringify(options.filters))

    return parts.join('_')
  }

  private buildQueryParams(options: CnstrcQueryOptions) {
    return {
      page           : options.page,
      resultsPerPage : options.numResults,
      sortBy         : options.sortBy,
      sortOrder      : options.sortOrder,
      filters        : options.filters as any,
    }
  }

  // ─── Recommendations ────────────────────────────────────────────────────────

  getCacheKey(podId: string, numResults: number, section?: string, itemIds?: string[], filters?: Record<string, string | string[]>): string {
    return `cnstrc-w_${podId}-count_${numResults}${section ? `-section_${section.replace(/ /g, '_')}` : ''}${itemIds?.length ? `-item_${itemIds.join('_')}` : ''}${filters ? `-filters_${JSON.stringify(filters)}` : ''}`
  }

  getRecommendations(podId: string, numResults = 12, section?: string, itemIds?: string[], filters?: Record<string, string | string[]>, options: CnstrcRecommendationOptions = {}): SyncOrAsync {
    const rogue = this.rogue
    if (!rogue) throw new Error('Init not called for Constructor service')
    if (!rogue.keys.constructor?.indexKey) {
      rogue.log.warn('Constructor.io API key not configured')

      return Promise.resolve(this.emptyResponse(podId))
    }

    const cacheKey = this.getCacheKey(podId, numResults, section, itemIds, filters)

    return this.fetchWithCache(
      cacheKey,
      `recommendations[${podId}]`,
      client => this.nodeFetch(client, rogue, podId, numResults, section, itemIds, filters),
      client => this.sdkFetch(client, podId, numResults, section, itemIds, filters),
      options.useSharedCache ?? true,
      options.fresh ?? false
    )
  }

  private async nodeFetch(client: INodeClient, rogue: IRogue, podId: string, numResults: number, section?: string, itemIds?: string[], filters?: Record<string, string | string[]>): Promise<IReflektionProcessedResponse> {
    const rawResponse = await client.recommendations.getRecommendations(
      podId,
      { numResults, section, ...(itemIds?.length ? { itemIds } : {}), ...(filters ? { filters } : {}) },
      this.buildNodeUserParams()
    )

    return this.processRecommendationsResponse(rawResponse, podId, section)
  }

  private async sdkFetch(client: ConstructorIOBrowser, podId: string, numResults: number, section?: string, itemIds?: string[], filters?: Record<string, string | string[]>): Promise<IReflektionProcessedResponse> {
    const rawResponse = await client.recommendations.getRecommendations(podId, { numResults, section, ...(itemIds?.length ? { itemIds } : {}), ...(filters ? { filters } : {}) } as any)

    return this.processRecommendationsResponse(rawResponse, podId, section)
  }

  private processRecommendationsResponse(rawResponse: RecommendationsResponse, podId: string, section?: string): IReflektionProcessedResponse {
    const rogue = this.rogue!

    if (section === SECTIONS.SEARCH_SUGGESTIONS) {
      const keyphrases = (rawResponse.response?.results || []).map((r: any) => ({
        text : r.value as string,
        freq : 0,
        id   : r.value as string,
      }))

      return {
        result_id   : rawResponse.result_id,
        content     : { product: { n_item: 0, total_item: 0, value: [] } },
        suggestion  : { category: [], keyphrase: keyphrases, trending_category: [] },
        facet       : {},
        facet_names : [],
        n_item      : 0,
        page_number : 1,
        total_item  : 0,
        total_page  : 1,
        widget      : { rfkid: podId },
      } as unknown as IReflektionProcessedResponse
    }

    const products = (rawResponse.response?.results || []).map((r: any) => {
      const product = processConstructorProduct(r, rogue)
      if (r.strategy?.id) (product as any).strategyId = r.strategy.id

      return product
    })
    const total = rawResponse.response?.total_num_results ?? products.length

    return {
      result_id   : rawResponse.result_id,
      pod         : rawResponse.response?.pod,
      content     : { product: { n_item: products.length, total_item: total, value: products } },
      facet       : {},
      facet_names : [],
      n_item      : products.length,
      page_number : 1,
      total_item  : total,
      total_page  : 1,
      widget      : { rfkid: podId },
    } as unknown as IReflektionProcessedResponse
  }

  // ─── Autocomplete ───────────────────────────────────────────────────────────

  getAutocompleteResults(query: string, numResults = 12, section?: string, pageNumber?: number): Promise<IReflektionProcessedResponse> {
    const rogue = this.rogue
    if (!rogue) throw new Error('Init not called for Constructor service')
    if (rogue.ssr || !query) return Promise.resolve(this.emptyResponse('autocomplete'))
    if (!rogue.keys.constructor?.indexKey) {
      rogue.log.warn('Constructor.io API key not configured')

      return Promise.resolve(this.emptyResponse('autocomplete'))
    }

    const cacheKey = `cnstrc-autocomplete_${query}_${numResults}${section ? `_${section}` : ''}${pageNumber ? `_p${pageNumber}` : ''}`
    if (this.inFlight[cacheKey] !== undefined) return this.inFlight[cacheKey]

    const clear = (response: IReflektionProcessedResponse) => {
      delete this.inFlight[cacheKey]

      return response
    }
    const fail = (err: any) => {
      delete this.inFlight[cacheKey]
      rogue.log.error('Constructor.io getAutocompleteResults failed', err)

      return Promise.reject(err)
    }

    this.inFlight[cacheKey] = (this.sdkClientPromise ?? Promise.resolve(this.sdkClient))
      .then(client => {
        if (!client) {
          rogue.log.warn('Constructor.io Browser SDK unavailable, skipping autocomplete fetch')

          return this.emptyResponse('autocomplete')
        }

        return this.sdkAutocompleteFetch(client, query, numResults, section, pageNumber)
      })
      .then(clear)
      .catch(fail)

    return this.inFlight[cacheKey]
  }

  private async sdkAutocompleteFetch(client: ConstructorIOBrowser, query: string, numResults: number, section?: string, pageNumber?: number): Promise<IReflektionProcessedResponse> {
    const resultsPerSection = section
      ? { [section]: numResults }
      : { [SECTIONS.PRODUCTS]: numResults, [SECTIONS.SEARCH_SUGGESTIONS]: SEARCH_SUGGESTION_COUNT }

    const params: IAutocompleteParameters = { resultsPerSection }
    if (pageNumber) params.qsParam = { page: pageNumber }

    const rawResponse = await client.autocomplete.getAutocompleteResults(query, params)

    return this.processAutocompleteResponse(rawResponse, section)
  }

  private processAutocompleteResponse(rawResponse: AutocompleteResponse, section?: string): IReflektionProcessedResponse {
    const rogue = this.rogue!
    const productItems = (rawResponse.sections?.[section ?? SECTIONS.PRODUCTS] || []) as any[]
    const suggestionItems = section ? [] : (rawResponse.sections?.[SECTIONS.SEARCH_SUGGESTIONS] || []) as any[]

    const products = productItems.map(item => processConstructorProduct({
      id           : item.data?.id || '',
      value        : item.value,
      data         : item.data || {},
      variation_id : item.data?.variation_id,
    }, rogue))

    const keyphrases = suggestionItems.map(item => ({
      text : item.value as string,
      freq : 0,
      id   : item.value as string,
    }))

    return {
      result_id   : rawResponse.result_id,
      content     : { product: { n_item: products.length, total_item: products.length, value: products } },
      suggestion  : { category: [], keyphrase: keyphrases, trending_category: [] },
      facet       : {},
      facet_names : [],
      n_item      : products.length,
      page_number : 1,
      total_item  : products.length,
      total_page  : 1,
      widget      : { rfkid: 'autocomplete' },
    } as unknown as IReflektionProcessedResponse
  }

  // ─── Search ─────────────────────────────────────────────────────────────────

  /**
   * One cheap search request behind the autosuggest input. Related searches only exist on the
   * search response — autocomplete has no equivalent — so the call that already resolves
   * redirects carries them out too rather than adding a second round trip per keystroke.
   */
  async getSearchPreflight(query: string): Promise<CnstrcSearchPreflight> {
    const empty: CnstrcSearchPreflight = { redirect: null, relatedSearches: [] }
    const rogue = this.rogue
    if (!rogue) throw new Error('Init not called for Constructor service')
    if (rogue.ssr || !query) return empty
    if (!rogue.keys.constructor?.indexKey) return empty

    const client = await (this.sdkClientPromise ?? Promise.resolve(this.sdkClient))
    if (!client) return empty

    try {
      const response = await client.search.getSearchResults(query, { resultsPerPage: 1 })
      const resp = response.response as any

      return {
        redirect        : resp?.redirect?.data?.url ?? null,
        relatedSearches : extractRelatedSearches(resp?.related_searches),
        resultId        : response.result_id,
      }
    } catch (err) {
      rogue.log.error('Constructor.io getSearchPreflight failed', err as Error)

      return empty
    }
  }

  getSearchResults(query: string, options: CnstrcQueryOptions = {}): SyncOrAsync {
    const rogue = this.rogue
    if (!rogue) throw new Error('Init not called for Constructor service')
    if (!query) return this.emptyResponse('search')
    if (!rogue.keys.constructor?.indexKey) {
      rogue.log.warn('Constructor.io API key not configured')

      return this.emptyResponse('search')
    }

    const cacheKey = this.buildQueryCacheKey(`cnstrc-search_${query}`, options)

    // Search results are deliberately kept out of Redis, mirroring RFK: queries are
    // effectively unique per user, so the hit rate approaches zero while every render
    // writes a full result set — and search is expected to reflect the freshest
    // catalog state. Browse and recommendations still cache; their key space is bounded.
    return this.fetchWithCache(
      cacheKey,
      'getSearchResults',
      client => this.nodeSearchFetch(client, rogue, query, options),
      client => this.sdkSearchFetch(client, query, options),
      false
    )
  }

  private async nodeSearchFetch(client: INodeClient, rogue: IRogue, query: string, options: CnstrcQueryOptions): Promise<IReflektionProcessedResponse> {
    const rawResponse = await client.search.getSearchResults(
      query,
      this.buildQueryParams(options),
      this.buildNodeUserParams()
    )
    const redirect = (rawResponse.response as any)?.redirect?.data?.url ?? null
    const result = this.processSearchBrowseResponse(rawResponse as any, redirect)

    return this.filterSearchFacets(result)
  }

  private async sdkSearchFetch(client: ConstructorIOBrowser, query: string, options: CnstrcQueryOptions): Promise<IReflektionProcessedResponse> {
    const response: SearchResponse = await client.search.getSearchResults(query, this.buildQueryParams(options))
    const redirect = (response.response as any)?.redirect?.data?.url ?? null
    const result = this.processSearchBrowseResponse(response as any, redirect)

    return this.filterSearchFacets(result)
  }

  private filterSearchFacets(result: IReflektionProcessedResponse): IReflektionProcessedResponse {
    const facet = Object.fromEntries(Object.entries(result.facet).filter(([key]) => SEARCH_FACET_ALLOWLIST.has(key)))

    return { ...result, facet, facet_names: Object.keys(facet) }
  }

  // ─── Browse ─────────────────────────────────────────────────────────────────

  getBrowseResults(filterName: string, filterValue: string, options: CnstrcQueryOptions = {}): SyncOrAsync {
    const rogue = this.rogue
    if (!rogue) throw new Error('Init not called for Constructor service')
    if (!filterValue) return this.emptyResponse('browse')
    if (!rogue.keys.constructor?.indexKey) {
      rogue.log.warn('Constructor.io API key not configured')

      return this.emptyResponse('browse')
    }

    const cacheKey = this.buildQueryCacheKey(`cnstrc-browse_${filterName}_${filterValue}`, options)

    return this.fetchWithCache(
      cacheKey,
      'getBrowseResults',
      client => this.nodeBrowseFetch(client, rogue, filterName, filterValue, options),
      client => this.sdkBrowseFetch(client, filterName, filterValue, options)
    )
  }

  private async nodeBrowseFetch(client: INodeClient, rogue: IRogue, filterName: string, filterValue: string, options: CnstrcQueryOptions): Promise<IReflektionProcessedResponse> {
    const rawResponse = await client.browse.getBrowseResults(
      filterName,
      filterValue,
      this.buildQueryParams(options),
      this.buildNodeUserParams()
    )

    return this.processSearchBrowseResponse(rawResponse as any, null)
  }

  private async sdkBrowseFetch(client: ConstructorIOBrowser, filterName: string, filterValue: string, options: CnstrcQueryOptions): Promise<IReflektionProcessedResponse> {
    const response: BrowseResponse<any, any> = await client.browse.getBrowseResults(filterName, filterValue, this.buildQueryParams(options))

    return this.processSearchBrowseResponse(response as any, null)
  }

  // ─── Response processors ────────────────────────────────────────────────────

  private processSearchBrowseResponse(rawResponse: any, redirectUrl: string | null): IReflektionProcessedResponse {
    const rogue = this.rogue!
    const resp = rawResponse.response || {}
    const products = (resp.results || []).map((r: any) => processConstructorProduct({
      id           : r.data?.id || '',
      value        : r.value,
      data         : r.data || {},
      variation_id : r.data?.variation_id,
    }, rogue))
    const total = resp.total_num_results ?? products.length
    const facet: Record<string, any> = {}

    for (const f of (resp.facets || [])) {
      if (f.type === 'range') continue // range facets have no options; checkbox UI can't render them

      let values: any[] = (f.options || []).map((opt: any) => ({
        text   : opt.display_name || opt.value,
        urlKey : opt.value,
        count  : opt.count,
      }))

      const modifyFn = modifyFacetValues[f.name]
      if (modifyFn) {
        values = modifyFn(values, rogue)
      }

      // modifyFacetValues.rating_summary sets urlKey = (stars - 1) = the number of gold stars shown,
      // giving clean, 1:1 URLs (e.g. ?rating_summary=4 for the "4 & Up" row). parseConstructorOptions
      // translates that threshold into Constructor's range-string filter value ("4"-"inf") at query time.

      const transformFn = facetValueTextTransform[f.name]
      if (transformFn) {
        values = values.map(v => ({ ...v, text: transformFn(v.text) }))
      }

      facet[f.name] = {
        key                : f.name,
        display_name       : normalizeFacetDisplayName(f.display_name || f.name, rogue),
        number_of_products : (f.options || []).reduce((sum: number, opt: any) => sum + (opt.count || 0), 0),
        value              : values,
        total              : values.length,
      }
    }

    rogue.log.debug(`Constructor facets (${resp.facets?.length ?? 0}): mapped keys=`, Object.keys(facet), 'raw=', resp.facets)

    const sort_options = (resp.sort_options || []).map((o: any) => ({
      sort_by      : o.sort_by,
      display_name : o.display_name,
      sort_order   : o.sort_order,
      status       : o.status,
    }))

    // Related searches ride on this same response. They are carried on the RFK-shaped
    // suggestion block the results page has always read.
    const relatedSearches = extractRelatedSearches(resp.related_searches).map(text => ({ text, freq: 0, id: text }))

    return {
      result_id      : rawResponse.result_id,
      cnstrc_request : rawResponse.request ?? null,
      redirect_url   : redirectUrl ?? undefined,
      content        : { product: { n_item: products.length, total_item: total, value: products } },
      suggestion     : { category: [], keyphrase: relatedSearches, trending_category: [] },
      facet,
      facet_names    : Object.keys(facet),
      sort_options,
      n_item         : products.length,
      page_number    : rawResponse.request?.page ?? 1,
      total_item     : total,
      total_page     : Math.ceil(total / (rawResponse.request?.num_results_per_page || 1)),
      widget         : { rfkid: 'search-browse' },
      cnstrc_groups  : resp.groups ?? [],
      // Merchandiser-authored content rules (banners / in-grid CTAs) matched for this request.
      cnstrc_content : processRefinedContent(resp.refined_content, rogue),
    } as unknown as IReflektionProcessedResponse
  }

  private emptyResponse(podId: string): IReflektionProcessedResponse {
    return {
      content     : { product: { n_item: 0, value: [], total_item: 0 } },
      facet       : {},
      facet_names : [],
      n_item      : 0,
      page_number : 0,
      total_item  : 0,
      total_page  : 0,
      widget      : { rfkid: podId },
    } as unknown as IReflektionProcessedResponse
  }
}
